import React, { useState } from "react";
import {
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Switch,
  Stack,
  alpha,
  Container,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Footer from "../Components/Footer";
import Navbar from "../Components/Navbar";
import PageTitle from "../Components/PageTitle";

const plans = [
  {
    name: "Free",
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: "For individuals and small teams just getting started.",
    features: [
      "Up to 10 integrations",
      "Basic analytics and reporting",
      "24/7 email support",
      "1 custom dashboard",
    ],
    isPopular: false,
  },
  {
    name: "Pro",
    monthlyPrice: 499,
    yearlyPrice: 4990,
    description: "For growing businesses that need more power and features.",
    features: [
      "Unlimited integrations",
      "Advanced analytics and reporting",
      "Priority email & chat support",
      "Unlimited custom dashboards",
      "API access for custom solutions",
    ],
    isPopular: true,
  },
  {
    name: "Enterprise",
    monthlyPrice: 999,
    yearlyPrice: 9990,
    description:
      "For large organizations with advanced security and support needs.",
    features: [
      "All Pro features, plus:",
      "Dedicated account manager",
      "Single Sign-On (SSO)",
      "Custom feature development",
      "99.9% uptime SLA",
    ],
    isPopular: false,
  },
];

export default function PricingPage() {
  const [billingCycle, setBillingCycle] = useState("monthly");

  const handleBillingChange = (event) => {
    setBillingCycle(event.target.checked ? "yearly" : "monthly");
  };

  return (
    <>
      <Navbar />
      <PageTitle
        title="Pricing - BizBridge"
        description="Explore BizBridge's flexible pricing plans designed to fit businesses of all sizes. Start for free and scale as you grow."
        keywords="Pricing, BizBridge Pricing Plans, Business Solutions Pricing"
        url="/pricing"
      />
      <Box sx={{ bgcolor: "#fff" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: { xs: "40vh", md: "50vh" },
            pt: { xs: 12, md: 12 },
            pb: { xs: 5, md: 5 },
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url("https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1920&q=80")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundColor: "rgba(10, 25, 41, 0.85)",
              backgroundBlendMode: "multiply",
              zIndex: 1,
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
            <Grid container spacing={5} alignItems="center">
              {/* CHANGE: Container ki chaudai 8 se 10 kar di hai */}
              <Grid item xs={12} md={10}>
                <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                  <Typography
                    variant="h1"
                    fontWeight={800}
                    gutterBottom
                    sx={{
                      color: "#fff",
                      textShadow: "1px 1px 5px rgba(0,0,0,0.3)",
                      fontSize: {
                        xs: "2.5rem",
                        sm: "3.5rem",
                        md: "4rem",
                        lg: "4.5rem",
                      },
                    }}
                  >
                    Simple, Transparent Pricing
                  </Typography>
                  <Typography
                    variant="h6"
                    component="p"
                    sx={{
                      color: "rgba(255,255,255,0.9)",
                      lineHeight: 1.8,
                      fontSize: { xs: "1rem", md: "1.25rem" },
                    }}
                  >
                    Choose the perfect plan for your business needs. Start for
                    free and scale as you grow. No hidden fees, no surprises.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
        {/* --- PRICING SECTION AB YAHAN SE SHURU HOTA HAI --- */}
        <Box
          component="main"
          sx={{
            py: { xs: 6, md: 10 },
            px: { xs: 2, md: 6 },
            fontFamily: "'Inter', sans-serif",
            bgcolor: "#fff",
          }}
        >
          {/* Billing Cycle Toggle */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ mb: 4 }}
          >
            <Typography
              fontWeight={billingCycle === "monthly" ? "bold" : "normal"}
              color={
                billingCycle === "monthly" ? "primary.main" : "text.secondary"
              }
            >
              Monthly
            </Typography>
            <Switch
              checked={billingCycle === "yearly"}
              onChange={handleBillingChange}
              inputProps={{ "aria-label": "billing cycle toggle" }}
            />
            <Typography
              fontWeight={billingCycle === "yearly" ? "bold" : "normal"}
              color={
                billingCycle === "yearly" ? "primary.main" : "text.secondary"
              }
            >
              Yearly
            </Typography>
            <Chip
              label="2 months free!"
              color="success"
              size="small"
              sx={{
                ml: 1,
                display: billingCycle === "yearly" ? "flex" : "none",
              }}
            />
          </Stack>

          {/* Pricing Cards Grid (Isme koi badlav nahi hai) */}
          <Grid
            container
            spacing={4}
            justifyContent="center"
            alignItems="stretch"
          >
            {plans.map((plan) => (
              <Grid item key={plan.name} xs={12} md={6} lg={4}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 4,
                    border: plan.isPopular ? "2px solid" : "1px solid",
                    borderColor: plan.isPopular ? "primary.main" : "#e5e7eb",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    bgcolor: plan.isPopular
                      ? alpha("#0b4064", 0.05)
                      : "#ffffff",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                    },
                    position: "relative",
                    overflow: "visible",
                  }}
                >
                  {plan.isPopular && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: -12,
                        left: "50%",
                        transform: "translateX(-50%)",
                        fontWeight: "bold",
                      }}
                    />
                  )}
                  <CardContent
                    sx={{
                      p: 4,
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="h5"
                        component="h2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {plan.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minHeight: "40px", mb: 2 }}
                      >
                        {plan.description}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "baseline", my: 2 }}
                      >
                        <Typography
                          variant="h3"
                          component="span"
                          fontWeight="bold"
                        >
                          ₹
                          {billingCycle === "monthly"
                            ? plan.monthlyPrice
                            : plan.yearlyPrice}
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          ml={1}
                        >
                          / {billingCycle === "monthly" ? "month" : "year"}
                        </Typography>
                      </Box>

                      {/* Features List */}
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          p: 0,
                          listStyle: "none",
                          textAlign: "left",
                          mt: 3,
                          mb: 4,
                        }}
                      >
                        {plan.features.map((feature, index) => (
                          <Typography
                            component="li"
                            key={index}
                            variant="body1"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1.5,
                            }}
                          >
                            <CheckCircleIcon
                              color="primary"
                              sx={{ fontSize: 22, mr: 1.5 }}
                            />
                            {feature}
                          </Typography>
                        ))}
                      </Box>
                    </Box>

                    {/* Action Button */}
                    <Button
                      fullWidth
                      variant={plan.isPopular ? "contained" : "outlined"}
                      size="large"
                      sx={{
                        borderRadius: 2,
                        fontWeight: "bold",
                        py: 1.5,
                        mt: "auto",
                        textTransform: "none",
                        fontSize: "1rem",
                      }}
                    >
                      {plan.name === "Free"
                        ? "Get Started"
                        : "Start Free Trial"}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
        <Footer /> {/* Footer added here */}
      </Box>
    </>
  );
}
